import { Modal } from 'antd';
import { COMMON_CONSTANTS } from '../store/oneButtonStore';

type PartnerRow = { Text?: string; Value?: string | number };

const asRecord = (value: unknown): Record<string, unknown> | null =>
    (value && typeof value === 'object' ? (value as Record<string, unknown>) : null);

const opportunityProductRows = (od: unknown): Record<string, unknown>[] => {
    const root = asRecord(od);
    if (!root) return [];
    const nested = asRecord(root.OpportunityDetails) ?? asRecord(root.opportunityDetails);
    const list =
        root.OpportunityProduct ??
        root.opportunityProduct ??
        root.opportunityProductList ??
        nested?.OpportunityProduct ??
        nested?.opportunityProduct ??
        nested?.opportunityProductList;
    return Array.isArray(list) ? list.filter((row): row is Record<string, unknown> => Boolean(asRecord(row))) : [];
};

const financePartnerLeadList = (od: unknown): Record<string, unknown>[] => {
    const root = asRecord(od);
    if (!root) return [];
    const nested = asRecord(root.OpportunityDetails) ?? asRecord(root.opportunityDetails);
    const inner = asRecord(root.data) ?? asRecord(root.Data);
    const list =
        root.FinancePartnerLeadList ??
        root.financePartnerLeadListModels ??
        root.financePartnerLeadList ??
        nested?.FinancePartnerLeadList ??
        nested?.financePartnerLeadListModels ??
        nested?.financePartnerLeadList ??
        inner?.FinancePartnerLeadList ??
        inner?.financePartnerLeadListModels ??
        inner?.financePartnerLeadList ??
        [];
    return Array.isArray(list) ? list.filter((row): row is Record<string, unknown> => Boolean(asRecord(row))) : [];
};

const isActive = (row: Record<string, unknown>): boolean => {
    const flag = row.ActiveFlag ?? row.activeFlag ?? row.Active_Ind ?? row.active_Ind;
    if (flag === false || flag === 0 || String(flag).toLowerCase() === 'false') return false;
    return true;
};

const partnerId = (row: Record<string, unknown>): string =>
    String(row.Finance_Partner_Id ?? row.finance_Partner_Id ?? row.FinancePartnerId ?? '');

const partnerDisplayKey = (value: unknown): string =>
    String(value ?? '')
        .replace(/\s+/g, '')
        .toLowerCase();

const findPartner = (partners: PartnerRow[], name: string): PartnerRow | undefined => {
    const want = partnerDisplayKey(name);
    if (!want) return undefined;
    return partners.find((el) => partnerDisplayKey(el.Text) === want);
};

const matchesPartner = (row: Record<string, unknown>, partner: PartnerRow | undefined): boolean => {
    if (!partner) return false;
    if (partner.Value != null && partner.Value !== '' && partnerId(row) === String(partner.Value)) return true;
    const want = partnerDisplayKey(partner.Text);
    if (!want) return false;
    const display = partnerDisplayKey(
        row.Finance_Partner_Display_Name ?? row.Finance_Partner ?? row.finance_Partner ?? '',
    );
    return display === want;
};

const isTrackedFinancePartner = (
    row: Record<string, unknown>,
    sunnova: PartnerRow | undefined,
    igs: PartnerRow | undefined,
    sunlight: PartnerRow | undefined,
    lightreach: PartnerRow | undefined,
): boolean => {
    if (
        matchesPartner(row, sunnova) ||
        matchesPartner(row, igs) ||
        matchesPartner(row, sunlight) ||
        matchesPartner(row, lightreach)
    ) {
        return true;
    }
    const display = partnerDisplayKey(
        row.Finance_Partner_Display_Name ??
            row.Finance_Partner ??
            row.finance_Partner ??
            row.Finance_Partner_Name ??
            '',
    );
    return (
        display.includes('sunnova') ||
        display === 'igs' ||
        display.startsWith('igs') ||
        display.includes('sunlight') ||
        display.includes('lightreach')
    );
};

const productTypeName = (row: Record<string, unknown>): string =>
    String(row.Product_Type_Name ?? row.product_Type_Name ?? row.ProductTypeName ?? row.ProductType ?? '').trim();

const productTypeKey = (row: Record<string, unknown>): string => productTypeName(row).toLowerCase();

const financeMethodName = (row: Record<string, unknown>): string =>
    String(row.Finance_Method_Name ?? row.finance_Method_Name ?? '');

const leadProductType = (row: Record<string, unknown>): string =>
    String(
        row.ProductTypeName ??
            row.productTypeName ??
            row.Product_Type_Name ??
            row.lead_Type ??
            row.Lead_Type ??
            '',
    )
        .trim()
        .toLowerCase();

const leadSyncId = (row: Record<string, unknown> | undefined): string =>
    String(
        row?.Finance_Partner_Sync_LeadID ??
            row?.FinancePartnerSyncLeadId ??
            row?.financePartnerSyncLeadId ??
            row?.finance_Partner_Sync_LeadID ??
            row?.LeadId ??
            row?.leadId ??
            '',
    ).trim();

const productPartnerSyncId = (row: Record<string, unknown>): string =>
    String(
        row.Finance_Partner_Sync_LeadID ??
            row.FinancePartnerSyncLeadId ??
            row.financePartnerSyncLeadId ??
            row.finance_Partner_Sync_LeadID ??
            '',
    ).trim();

const findLead = (
    leads: Record<string, unknown>[],
    matcher: (name: string) => boolean,
): Record<string, unknown> | undefined => leads.find((el) => matcher(leadProductType(el)));

/**
 * V1 `opportunity-info.getFinancePartnerProductList` —
 * active Sunnova / IGS / Sunlight / Lightreach products with no sync lead id.
 */
export function getFinancePartnerProductList(
    opportunityDetails: unknown,
    financePartnersList: PartnerRow[] | null | undefined,
    extraProducts?: unknown[],
): string {
    const partners = Array.isArray(financePartnersList) ? financePartnersList : [];
    const sunnova = findPartner(partners, COMMON_CONSTANTS.SunnovaPartnerName);
    const igs = findPartner(partners, COMMON_CONSTANTS.IGSPartnerName);
    const sunlight = findPartner(partners, COMMON_CONSTANTS.SunlightPartnerName);
    const lightreach = findPartner(partners, COMMON_CONSTANTS.LightreachPartnerName);
    const products: string[] = [];
    const leads = financePartnerLeadList(opportunityDetails);
    const extraRows = Array.isArray(extraProducts)
        ? extraProducts.filter((row): row is Record<string, unknown> => Boolean(asRecord(row)))
        : [];
    const rows = [...opportunityProductRows(opportunityDetails), ...extraRows];

    rows.forEach((ele) => {
        const typeKey = productTypeKey(ele);
        const typeName = productTypeName(ele);
        let productDetails: Record<string, unknown> | undefined;
        if (typeKey === 'solar') {
            productDetails = findLead(leads, (name) => name === 'solar');
        } else if (typeKey === 'battery' || typeKey === 'battery only') {
            productDetails = findLead(leads, (name) => name === 'battery' || name === 'battery only');
        } else if (typeKey === 'roof' || typeKey === 'roof only') {
            productDetails = findLead(leads, (name) => name === 'roof' || name === 'roof only');
        } else if (typeKey === 'rrr') {
            productDetails = findLead(leads, (name) => name === 'rrr');
        } else {
            return;
        }
        const isTrackedPartner = isTrackedFinancePartner(ele, sunnova, igs, sunlight, lightreach);
        if (isTrackedPartner && isActive(ele) && !leadSyncId(productDetails) && !productPartnerSyncId(ele)) {
            const label = typeName || typeKey;
            if (label && !products.includes(label)) products.push(label);
        }
    });
    return products.join(',');
}

/**
 * V1 `opportunity-info.haveProductLead` after `checkLeadDetailAPI`.
 * True when a tracked product still has no finance-partner sync lead.
 */
export function haveProductLead(
    opportunityDetails: unknown,
    financePartnersList: PartnerRow[] | null | undefined,
): boolean {
    const partners = Array.isArray(financePartnersList) ? financePartnersList : [];
    if (partners.length === 0 && opportunityProductRows(opportunityDetails).length === 0) return false;
    const sunnova = findPartner(partners, COMMON_CONSTANTS.SunnovaPartnerName);
    const igs = findPartner(partners, COMMON_CONSTANTS.IGSPartnerName);
    const sunlight = findPartner(partners, COMMON_CONSTANTS.SunlightPartnerName);
    const lightreach = findPartner(partners, COMMON_CONSTANTS.LightreachPartnerName);
    const rows = opportunityProductRows(opportunityDetails);
    const leads = financePartnerLeadList(opportunityDetails);

    const solarDetails = rows.find(
        (ele) =>
            productTypeKey(ele) === 'solar' &&
            isTrackedFinancePartner(ele, sunnova, igs, sunlight, lightreach) &&
            isActive(ele),
    );
    const isSunnovaProduct = (ele: Record<string, unknown>): boolean =>
        matchesPartner(ele, sunnova) ||
        partnerDisplayKey(ele.Finance_Partner_Display_Name ?? ele.Finance_Partner) ===
            partnerDisplayKey(COMMON_CONSTANTS.SunnovaPartnerName);

    const roofOnlyDetails = rows.find(
        (ele) =>
            (productTypeKey(ele) === 'roof only' || productTypeKey(ele) === 'roof') &&
            isSunnovaProduct(ele) &&
            isActive(ele),
    );
    const rrrDetails = rows.find(
        (ele) => productTypeKey(ele) === 'rrr' && isSunnovaProduct(ele) && isActive(ele),
    );
    const batteryOnlyDetails = rows.find(
        (ele) =>
            (productTypeKey(ele) === 'battery only' || productTypeKey(ele) === 'battery') &&
            (matchesPartner(ele, sunnova) ||
                partnerDisplayKey(ele.Finance_Partner_Display_Name ?? ele.Finance_Partner) ===
                    partnerDisplayKey(COMMON_CONSTANTS.SunnovaPartnerName) ||
                ((matchesPartner(ele, lightreach) ||
                    partnerDisplayKey(ele.Finance_Partner_Display_Name ?? ele.Finance_Partner).includes('lightreach')) &&
                    partnerDisplayKey(financeMethodName(ele)) === partnerDisplayKey('Battery Lease'))) &&
            isActive(ele),
    );

    let haveSolarLead = !solarDetails;
    let haveRoofOnlyLead = !roofOnlyDetails;
    let haveRRRLead = !rrrDetails;
    let haveBatteryOnlyLead = !batteryOnlyDetails;

    const solarSync = leadSyncId(findLead(leads, (name) => name === 'solar'));
    const roofSync = leadSyncId(findLead(leads, (name) => name === 'roof' || name === 'roof only'));
    const rrrSync = leadSyncId(findLead(leads, (name) => name === 'rrr'));
    const batterySync = leadSyncId(findLead(leads, (name) => name === 'battery' || name === 'battery only'));

    if (solarDetails && solarSync) haveSolarLead = true;
    if (roofOnlyDetails && roofSync) haveRoofOnlyLead = true;
    if (rrrDetails && rrrSync) haveRRRLead = true;
    if (batteryOnlyDetails && batterySync) haveBatteryOnlyLead = true;

    return !haveSolarLead || !haveRoofOnlyLead || !haveRRRLead || !haveBatteryOnlyLead;
}

/** V1 `LeadCheckGuard` name string: Sunnova and/or IGS only. */
const sunnovaIgsPartnerLabel = (
    opportunityDetails: unknown,
    financePartnersList: PartnerRow[] | null | undefined,
): string => {
    const partners = Array.isArray(financePartnersList) ? financePartnersList : [];
    const sunnova = findPartner(partners, COMMON_CONSTANTS.SunnovaPartnerName);
    const igs = findPartner(partners, COMMON_CONSTANTS.IGSPartnerName);
    const rows = opportunityProductRows(opportunityDetails).map((ele) => {
        const match = partners.find((el) => String(el.Value) === partnerId(ele));
        return { ...ele, Finance_Partner: match?.Text || ele.Finance_Partner };
    });
    const sunnovaRow = rows.find((product) => product.Finance_Partner === COMMON_CONSTANTS.SunnovaPartnerName);
    const igsRow = rows.find((product) => product.Finance_Partner === COMMON_CONSTANTS.IGSPartnerName);
    const sunnovaName = sunnovaRow ? sunnova?.Text || '' : '';
    const igsName = igsRow ? igs?.Text || '' : '';
    if (sunnovaName && igsName) return `${sunnovaName} and ${igsName}`;
    return sunnovaName || igsName || '';
};

let lastStripeSyncPromptAt = 0;

/** V1 Opportunity Info → Stripe: unsynced Sunnova/IGS/Lightreach/Sunlight products. */
export function stripeFinancePartnerSyncMessage(
    opportunityDetails: unknown,
    financePartnersList: PartnerRow[] | null | undefined,
    extraProducts?: unknown[],
): string | null {
    const productList = getFinancePartnerProductList(opportunityDetails, financePartnersList, extraProducts);
    if (productList) {
        const financePartner = sunnovaIgsPartnerLabel(opportunityDetails, financePartnersList);
        return `You have chosen ${financePartner} as finance partner for the following product(s) (${productList}),
        you need to “Sync with Finance Partner before proceeding.
        Please click on Finance Partner Sync” button or remove current finance partner selection to proceed.`;
    }
    if (haveProductLead(opportunityDetails, financePartnersList)) {
        return 'Finance Partner You have chosen is not saved.Please save to continue';
    }
    return null;
}

export function showStripeFinancePartnerSyncModal(message: string, theme: string): void {
    const now = Date.now();
    if (now - lastStripeSyncPromptAt < 800) return;
    lastStripeSyncPromptAt = now;
    Modal.info({
        title: ' ',
        content: message,
        okText: 'OK',
        centered: true,
        closable: false,
        zIndex: 20000,
        className: `obd-quote-design-blocked-modal obd-theme-${theme}`,
        rootClassName: `obd-theme-${theme}`,
        getContainer: () => document.body,
    });
}
